import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import Realisations from './components/Realisations';
import Reviews from './components/Reviews';
import LatestArticles from './components/LatestArticles';
import ReservationForm from './components/ReservationForm';
import Contact from './components/Contact';
import Footer from './components/Footer';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './pages/AuthCallback';
import BlogList from './pages/BlogList';
import BlogPostPage from './pages/BlogPostPage';
import CreditImpotPage from './pages/CreditImpotPage';
import LocalSeoPage from './pages/LocalSeoPage';
import LegalPage from './pages/LegalPage';
import { CookieBanner } from './components/CookieBanner';
import { LOCAL_PAGES } from './data/localPages';
import ZonesIntervention from './components/ZonesIntervention';
import FAQ from './components/FAQ';
import { useDocumentMeta } from './lib/useDocumentMeta';
import { trackPageView, trackContact, trackWhatsApp } from './lib/pixel';

const Home = () => {
  const location = useLocation();
  useDocumentMeta({
    title: 'Nettoyage de vitres & ménage professionnel à Royan — SMT Services Charente-Maritime',
    description:
      "Nettoyage de vitres et ménage professionnel à Royan, La Tremblade, Saint-Palais-sur-Mer et Charente-Maritime. Devis gratuit sous 24h, intervention rapide, crédit d'impôt 50 % pour les particuliers. 4,8/5 sur Google.",
    path: '/',
    ogType: 'website',
  });
  // Scroll to hash target on mount / hash change (for cross-page #section links)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [location.hash]);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <Hero />
        <Services />
        <Realisations />
        <ZonesIntervention />
        <Reviews />
        <FAQ />
        <LatestArticles />
        <ReservationForm />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

// Detect OAuth callback in URL fragment BEFORE routing so session_id is processed first
const AppRouter = () => {
  const location = useLocation();

  // Track PageView on every SPA route change. The Pixel snippet in index.html
  // only initializes the Pixel (no PageView there) — React fires PageView on
  // the initial render + every subsequent route change.
  // We deduplicate by URL to absorb React StrictMode double-invoke in dev
  // and any other accidental double-render.
  const lastTrackedRef = React.useRef('');
  useEffect(() => {
    const url = location.pathname + location.search;
    if (lastTrackedRef.current === url) return;
    lastTrackedRef.current = url;
    trackPageView();
  }, [location.pathname, location.search]);

  // Delegated click listener — tracks tel: (Contact) and WhatsApp clicks
  // anywhere in the React tree without touching every individual button.
  useEffect(() => {
    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      if (href.startsWith('tel:')) {
        trackContact({ method: 'phone', destination: href.replace('tel:', '') });
      } else if (/^https?:\/\/(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com|chat\.whatsapp\.com)/i.test(href)) {
        trackWhatsApp({ destination: href });
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/credit-impot-50-particuliers" element={<CreditImpotPage />} />
      <Route path="/politique-de-confidentialite" element={<LegalPage key="pc" path="/politique-de-confidentialite" />} />
      <Route path="/conditions-generales-utilisation" element={<LegalPage key="cgu" path="/conditions-generales-utilisation" />} />
      {Object.keys(LOCAL_PAGES).map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<LocalSeoPage />} />
      ))}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <CookieBanner />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
